import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Label } from './ui/label';
import { Eye, EyeOff, Chrome, Apple, Facebook, Linkedin, Twitter, Mail, LogIn, Lock, ArrowRight } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const success = login(email, password);
    if (success) {
      navigate('/dashboard');
    } else {
      setError('Invalid email or password');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Illustration */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute rounded-full bg-white w-80 h-80 -top-40 -left-20"></div>
          <div className="absolute rounded-full bg-white w-64 h-64 bottom-0 -right-20"></div>
          <div className="absolute rounded-full bg-white w-96 h-96 -bottom-40 left-40"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-md">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                className="w-[250px] h-auto transition-transform hover:scale-105" 
                src="https://bsquaress.com/assest/logo.png" 
                alt="BSQUARE Logo"
              />
            </div>
          <h2 className="text-4xl font-bold mb-6">Welcome to BSQUARE</h2>
          <p className="text-lg opacity-90 mb-8">Manage better, serve faster, grow stronger.
 </p>
          
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-2xl">
            <div className="flex justify-center mb-6">
              <div className="flex items-center justify-center w-16 h-16 bg-white/20 rounded-full">
                <Lock className="h-8 w-8 text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-2">Secure Login</h3>
            <p className="opacity-80">Your data is encrypted and securely stored</p>
          </div>
        </div>
        
        <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm">
          © 2025 BSQUARE Corporation Pvt. Ltd. All Rights Reserved.
        </div>
      </div>
      
      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                className="w-40 h-auto transition-transform hover:scale-105" 
                src="https://bsquaress.com/assest/logo.png" 
                alt="BSQUARE Logo"
              />
            </div>
            <p className="text-gray-600">Sign in to access your account</p>
          </div>

          <Card className="shadow-2xl border-0 rounded-2xl overflow-hidden backdrop-blur-sm bg-white/95">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-1 w-full"></div>
            <CardHeader className="space-y-1 pb-4 pt-8">
              <div className="flex justify-center mb-2">
                <div className="p-3 bg-blue-100 rounded-full">
                  <LogIn className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-center text-gray-900">
                Welcome Back
              </CardTitle>
              <p className="text-sm text-gray-600 text-center">
                Enter your credentials to continue your journey
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email address</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className={`h-5 w-5 transition-colors ${isFocused.email ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setIsFocused({...isFocused, email: true})}
                      onBlur={() => setIsFocused({...isFocused, email: false})}
                      className="h-12 pl-10 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                    <button 
                      type="button" 
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className={`h-5 w-5 transition-colors ${isFocused.password ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setIsFocused({...isFocused, password: true})}
                      onBlur={() => setIsFocused({...isFocused, password: false})}
                      className="h-12 pl-10 pr-10 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {error && (
                  <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg border border-red-100 flex items-center justify-center animate-pulse">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    <span className="flex items-center justify-center">
                      Sign In
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-11 py-1 px-0 flex items-center justify-center rounded-lg border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors">
                  <Chrome className="h-5 w-5 text-gray-700 mr-2" />
                  Google
                </Button>
                <Button variant="outline" className="h-11 py-1 px-0 flex items-center justify-center rounded-lg border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors">
                  <Apple className="h-5 w-5 text-gray-700 mr-2" />
                  Apple
                </Button>
              </div>
            </CardContent>
            <CardFooter className="flex justify-center border-t border-gray-100 bg-gray-50/70 py-6">
              <div className="text-sm text-gray-600">
                Don't have an account?{' '}
                <button className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
                  Sign up now
                </button>
              </div>
            </CardFooter>
          </Card>

          <div className="lg:hidden text-center mt-8 text-xs text-gray-500">
            © 2025. BSQUARE Corporation Pvt. Ltd. All Rights Reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;